import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarRange, Download, Upload, Save, Sparkles, AlertTriangle, Users, ChevronDown, ChevronUp, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { isoToDisplay } from "../components/DateInput";
import { MultiNamaInput, BarisNama, pecahNama } from "../components/MultiNamaInput";
import { KirimJadwalWA } from "../components/KirimJadwalWA";
import { RekapBebanTahunan } from "../components/RekapBebanTahunan";
import { PresensiPelayanDialog } from "../components/PresensiPelayanDialog";
import { RekapPresensiPelayan } from "../components/RekapPresensiPelayan";
import { downloadFile } from "../lib/download";
import { namaGelar } from "../lib/utils";

const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const REKAP_ROLES = ["ADMIN", "PIMPINAN", "SEKRETARIAT", "KOORDINATOR_IBADAH", "MAJELIS_PENATALAYANAN"];
const PRESENSI_WRITE_ROLES = ["ADMIN", "SEKRETARIAT", "KOORDINATOR_IBADAH"];
const PRESENSI_READ_ROLES = [...PRESENSI_WRITE_ROLES, "PIMPINAN", "CARE_LEADER"];

const lev = (a, b) => {
  a = a.toLowerCase(); b = b.toLowerCase();
  const m = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j++) m[0][j] = j;
  for (let i = 1; i <= a.length; i++) for (let j = 1; j <= b.length; j++) m[i][j] = Math.min(m[i - 1][j] + 1, m[i][j - 1] + 1, m[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return m[a.length][b.length];
};
// Saran terdekat: cocokkan token nama (mis. "Yusmita") lalu jarak Levenshtein.
export const saranTerdekat = (nama, daftar, n = 3) => {
  const tok = nama.toLowerCase().split(/\s+/).filter((t) => t.length > 2);
  const skorTok = (s) => tok.reduce((a, t) => a + Math.max(...s.toLowerCase().split(/\s+/).map((w) => (lev(t, w) <= Math.max(1, Math.floor(t.length / 4)) ? 8 : 0))), 0);
  return daftar
    .map((s) => ({ s, skor: lev(nama, s) - skorTok(s) }))
    .sort((x, y) => x.skor - y.skor).slice(0, n).map((x) => x.s);
};

// Pemetaan posisi jadwal -> bidang pelayanan (tab Gerejawi). Posisi tanpa bidang baku dicocokkan ke "Pelayanan Lainnya".
const BIDANG_POSISI = {
  Pembicara: ["Pengkhotbah"], "Worship Leader": ["Worship Leader"], Singer: ["Singer"], Keyboard: ["Keyboard"], Guitar: ["Guitar", "Gitar"],
  Bass: ["Bass"], Drum: ["Drum"], "Music Director": ["Music Director"], Usher: ["Usher", "Penerima Tamu"], Persembahan: ["Persembahan"],
  Multimedia: ["Multimedia"], Broadcast: ["Broadcast"], Soundman: ["Sound System"], Dancer: ["Dancer"], "PIC Ibadah": ["PIC Ibadah"], "Perjamuan Kudus": ["Perjamuan Kudus"],
};
export const saranUntukPosisi = (posisi, saran) => {
  if (posisi === "Tema") return [];
  const dasar = posisi.replace(/\s+\d+$/, "");
  const bidang = BIDANG_POSISI[dasar];
  const cocokLain = (s) => (s.lainnya || "").toLowerCase().includes(dasar.toLowerCase());
  if (!bidang) return saran.filter(cocokLain);
  return saran.filter((s) => (s.bidang || []).some((b) => bidang.includes(b)));
};

export default function JadwalPelayananPage() {
  const { can, user } = useAuth();
  const canWrite = can("jadwal:write");
  const [params, setParams] = useSearchParams();
  const qTgl = params.get("tanggal");
  const now = new Date();
  const [bulan, setBulan] = useState(qTgl ? Number(qTgl.slice(5, 7)) : now.getMonth() + 1);
  const [tahun, setTahun] = useState(qTgl ? Number(qTgl.slice(0, 4)) : now.getFullYear());
  const [fokus, setFokus] = useState(qTgl && params.get("fokus") ? { tanggal: qTgl, posisi: params.get("fokus"), nama: params.get("nama") || "" } : null);
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saran, setSaran] = useState([]);
  const [saving, setSaving] = useState(false);
  const [bebanBuka, setBebanBuka] = useState(false);
  const [presensiTgl, setPresensiTgl] = useState(null);
  const [presensiRefresh, setPresensiRefresh] = useState(0);
  const userRoles = user?.roles?.length ? user.roles : [user?.role];
  const canPresensi = PRESENSI_WRITE_ROLES.some((r) => userRoles.includes(r));
  const canRekapPresensi = PRESENSI_READ_ROLES.some((r) => userRoles.includes(r));
  const fileRef = useRef(null);
  useEffect(() => { if (canWrite) api.get("/jadwal/nama-pelayan").then((r) => setSaran(r.data.nama)).catch(() => {}); }, [canWrite]);

  const load = async () => {
    try {
      const r = await api.get("/jadwal", { params: { bulan, tahun } });
      setData(r.data);
      setDraft(Object.fromEntries(r.data.minggu.map((m) => [m.tanggal, { ...m.isi }])));
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal memuat jadwal"); }
  };
  useEffect(() => { load(); }, [bulan, tahun]); // eslint-disable-line react-hooks/exhaustive-deps

  const setCell = (tgl, posisi, v) => setDraft((d) => ({ ...d, [tgl]: { ...d[tgl], [posisi]: v } }));
  useEffect(() => {
    if (!fokus || !data) return;
    const el = document.querySelector(`[data-testid="jadwal-cell-${fokus.tanggal}-${fokus.posisi.replace(/\s+/g, "-")}"]`);
    if (el) { el.scrollIntoView({ block: "center", inline: "center" }); el.focus(); }
  }, [fokus, data]);
  const terapkanSaran = (s) => {
    const cur = draft[fokus.tanggal]?.[fokus.posisi] || "";
    const next = fokus.nama && cur.toLowerCase().includes(fokus.nama.toLowerCase()) ? cur.replace(new RegExp(fokus.nama.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i"), s) : s;
    setCell(fokus.tanggal, fokus.posisi, next);
    setFokus({ ...fokus, nama: s, selesai: true });
  };
  const tutupFokus = () => { setFokus(null); setParams({}, { replace: true }); };

  // Bentrok: satu nama di >1 posisi pada minggu yang sama (Tema & Seragam bukan nama orang).
  const bentrok = React.useMemo(() => {
    const out = {};
    if (!data) return out;
    for (const m of data.minggu) {
      const isi = canWrite ? draft[m.tanggal] || {} : m.isi || {};
      const peta = {};
      for (const p of data.posisi) { if (p === "Tema" || p === "Seragam") continue; for (const n of pecahNama(isi[p])) { const k = n.toLowerCase(); (peta[k] ||= { nama: n, posisi: [] }).posisi.push(p); } }
      const MUSISI = ["Keyboard 1", "Keyboard 2", "Bass", "Guitar 1", "Guitar 2", "Drum"];
      const PK_BOLEH = ["Usher", "Persembahan", "PIC Ibadah"];
      // Pengecualian: PIC Ibadah bebas; Music Director + musisi; Perjamuan Kudus + Usher/Persembahan/PIC Ibadah.
      const efektif = (ps) => {
        let a = ps.filter((p) => p !== "PIC Ibadah");
        if (a.includes("Music Director") && a.filter((p) => p !== "Music Director").every((p) => MUSISI.includes(p))) a = a.filter((p) => p !== "Music Director");
        if (a.includes("Perjamuan Kudus") && ps.filter((p) => p !== "Perjamuan Kudus").every((p) => PK_BOLEH.includes(p))) a = a.filter((p) => p !== "Perjamuan Kudus");
        return a;
      };
      const dup = Object.values(peta).filter((x) => efektif(x.posisi).length > 1);
      if (dup.length) out[m.tanggal] = dup;
    }
    return out;
  }, [data, draft, canWrite]);
  const selBentrok = (tgl, p) => (bentrok[tgl] || []).some((x) => x.posisi.includes(p));
  // Beban pelayan: berapa kali tiap nama dijadwalkan dalam sebulan.
  const beban = React.useMemo(() => {
    if (!data) return [];
    const peta = {};
    for (const m of data.minggu) {
      const isi = canWrite ? draft[m.tanggal] || {} : m.isi || {};
      for (const p of data.posisi) { if (p === "Tema" || p === "Seragam") continue; for (const n of pecahNama(isi[p])) { const k = n.toLowerCase(); (peta[k] ||= { nama: n, n: 0, posisi: new Set() }).n++; peta[k].posisi.add(p); } }
    }
    return Object.values(peta).sort((a, b) => b.n - a.n || a.nama.localeCompare(b.nama));
  }, [data, draft, canWrite]);

  const save = async () => {
    setSaving(true);
    try {
      for (const m of data.minggu) await api.put(`/jadwal/${m.tanggal}`, { isi: draft[m.tanggal] || {} });
      toast.success("Jadwal pelayanan disimpan");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); } finally { setSaving(false); }
  };

  const template = async () => {
    try { await downloadFile("/jadwal/template/excel", { bulan, tahun }, `jadwal_pelayanan_${tahun}-${String(bulan).padStart(2, "0")}.xlsx`); }
    catch { toast.error("Gagal mengunduh template"); }
  };

  const importFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/jadwal/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import selesai: ${r.data.jumlah_minggu} minggu`);
      r.data.peringatan?.forEach((p) => toast.warning(p));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Import gagal"); } finally { e.target.value = ""; }
  };

  return (
    <div className="space-y-4" data-testid="jadwal-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2"><CalendarRange className="h-7 w-7 text-[hsl(224,64%,33%)]" />Jadwal Pelayanan</h1>
          <p className="text-sm text-slate-500 mt-1">Daftar pelayan per minggu berdasarkan tanggal ibadah. Nama pada jadwal menjadi peserta Absensi Doa Persiapan Pelayan IBR.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-xl min-h-[44px]" onClick={template} data-testid="jadwal-template-btn"><Download className="h-4 w-4 mr-2" />Template Excel</Button>
          {canWrite && <>
            <Button variant="outline" className="rounded-xl min-h-[44px]" onClick={() => fileRef.current?.click()} data-testid="jadwal-import-btn"><Upload className="h-4 w-4 mr-2" />Import Excel</Button>
            <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={importFile} data-testid="jadwal-import-file" />
            <Button className="rounded-xl min-h-[44px] bg-[hsl(224,64%,33%)] hover:bg-[hsl(224,64%,28%)]" onClick={save} disabled={saving || !data} data-testid="jadwal-save-btn"><Save className="h-4 w-4 mr-2" />{saving ? "Menyimpan..." : "Simpan"}</Button>
          </>}
        </div>
      </div>
      <Card className="p-3 rounded-2xl flex flex-wrap gap-2 items-center">
        <select value={bulan} onChange={(e) => setBulan(Number(e.target.value))} className="min-h-[44px] rounded-xl border px-3 text-sm" data-testid="jadwal-bulan">
          {BULAN.map((b, i) => <option key={b} value={i + 1}>{b}</option>)}
        </select>
        <Input type="number" value={tahun} onChange={(e) => setTahun(Number(e.target.value))} className="min-h-[44px] rounded-xl w-28" data-testid="jadwal-tahun" />
        {data && <span className="text-xs text-slate-500 ml-auto" data-testid="jadwal-info">{data.minggu.length} Minggu · {canWrite ? `Klik sel untuk mengisi · ${saran.length} saran nama pelayan` : "Hanya lihat"}</span>}
      </Card>
      {fokus && canWrite && (
        <Card className="p-3 rounded-2xl border-amber-200 bg-amber-50 flex flex-wrap items-center gap-2" data-testid="jadwal-fokus-panel">
          <Sparkles className="h-4 w-4 text-amber-700" />
          <span className="text-sm text-amber-900">Perbaiki <b>{fokus.posisi}</b> Minggu {isoToDisplay(fokus.tanggal)}{fokus.selesai ? <> — diganti menjadi <b>"{fokus.nama}"</b>. Klik <b>Simpan</b> untuk menyimpan.</> : fokus.nama && <> — nama <b>"{fokus.nama}"</b> belum cocok. Saran terdekat:</>}</span>
          {!fokus.selesai && fokus.nama && saranTerdekat(fokus.nama, saranUntukPosisi(fokus.posisi, saran).map((s) => s.nama)).map((s) => (
            <button key={s} type="button" onClick={() => terapkanSaran(s)} className="px-3 py-1.5 rounded-full bg-white border border-amber-300 text-sm font-semibold text-amber-900 hover:bg-amber-100 min-h-[36px]" data-testid={`jadwal-saran-${s.replace(/\s+/g, "-")}`}>{s}</button>
          ))}
          <button type="button" onClick={tutupFokus} className="ml-auto text-xs text-amber-800 underline min-h-[36px]" data-testid="jadwal-fokus-tutup">Tutup</button>
        </Card>
      )}
      {data && Object.keys(bentrok).length > 0 && (
        <Card className="p-3 rounded-2xl border-red-200 bg-red-50 space-y-1" data-testid="jadwal-bentrok-panel">
          <div className="flex items-center gap-2 text-sm font-semibold text-red-800"><AlertTriangle className="h-4 w-4" />Bentrok pelayan terdeteksi — satu nama dijadwalkan di lebih dari satu posisi pada minggu yang sama:</div>
          {Object.entries(bentrok).map(([tgl, list]) => list.map((x) => (
            <div key={`${tgl}-${x.nama}`} className="text-sm text-red-900 pl-6" data-testid={`jadwal-bentrok-${tgl}-${x.nama.replace(/\s+/g, "-")}`}>Minggu {isoToDisplay(tgl)}: <b>{x.nama}</b> → {x.posisi.join(", ")}</div>
          )))}
        </Card>
      )}
      {data && (
        <Card className="rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="jadwal-table">
            <thead className="bg-slate-50 text-slate-600">
              <tr><th className="p-3 text-left sticky left-0 bg-slate-50 w-40">Posisi</th>
                {data.minggu.map((m) => <th key={m.tanggal} className="p-3 text-left whitespace-nowrap" data-testid={`jadwal-col-${m.tanggal}`}><div className="flex items-center gap-2">Minggu, {isoToDisplay(m.tanggal)}<KirimJadwalWA minggu={canWrite ? { ...m, isi: { ...m.isi, ...(draft[m.tanggal] || {}) } } : m} posisi={data.posisi} testId={`jadwal-wa-${m.tanggal}`} /></div>
                  {canPresensi && <button type="button" onClick={() => setPresensiTgl(m.tanggal)} className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 px-2.5 min-h-[30px] text-xs font-semibold hover:bg-emerald-100" data-testid={`jadwal-presensi-btn-${m.tanggal}`}><ClipboardCheck className="h-3.5 w-3.5" />Catat Presensi</button>}</th>)}</tr>
            </thead>
            <tbody>
              {data.posisi.map((p) => (
                <tr key={p} className="border-t">
                  <td className="p-2 font-medium text-slate-700 sticky left-0 bg-white">{p}</td>
                  {data.minggu.map((m) => (
                    <td key={m.tanggal} className={`p-1 ${selBentrok(m.tanggal, p) ? "bg-red-50 ring-1 ring-inset ring-red-300" : ""}`} title={selBentrok(m.tanggal, p) ? "Nama ini juga dijadwalkan di posisi lain pada minggu yang sama" : undefined} data-testid={selBentrok(m.tanggal, p) ? `jadwal-sel-bentrok-${m.tanggal}-${p.replace(/\s+/g, "-")}` : undefined}>
                      {canWrite
                        ? (data.multi_maks?.[p]
                          ? <MultiNamaInput value={draft[m.tanggal]?.[p] || ""} onChange={(v) => setCell(m.tanggal, p, v)} maks={data.multi_maks[p]} saran={saranUntukPosisi(p, saran)} highlight={!!(fokus && fokus.tanggal === m.tanggal && fokus.posisi === p)} testId={`jadwal-cell-${m.tanggal}-${p.replace(/\s+/g, "-")}`} />
                          : !["Tema", "Seragam"].includes(p)
                          ? <BarisNama value={draft[m.tanggal]?.[p] || ""} onChange={(v) => setCell(m.tanggal, p, v)} onHapus={() => setCell(m.tanggal, p, "")} placeholder={p === "PIC Ibadah" ? "Nama PIC" : "Nama"} saran={saranUntukPosisi(p, saran)} highlight={!!(fokus && fokus.tanggal === m.tanggal && fokus.posisi === p)} testId={`jadwal-cell-${m.tanggal}-${p.replace(/\s+/g, "-")}`} />
                          : <Input list={`jadwal-saran-${p.replace(/\s+/g, "-")}`} value={draft[m.tanggal]?.[p] || ""} onChange={(e) => setCell(m.tanggal, p, e.target.value)} className={`min-h-[40px] rounded-lg ${fokus && fokus.tanggal === m.tanggal && fokus.posisi === p ? "ring-2 ring-amber-400 bg-amber-50" : ""}`} data-testid={`jadwal-cell-${m.tanggal}-${p.replace(/\s+/g, "-")}`} />)
                        : (data.multi_maks?.[p] && pecahNama(m.isi?.[p]).length > 1
                          ? <ol className="px-2 py-1 list-decimal list-inside space-y-0.5" data-testid={`jadwal-view-${m.tanggal}-${p.replace(/\s+/g, "-")}`}>{pecahNama(m.isi?.[p]).map((n, i) => <li key={i}>{n}</li>)}</ol>
                          : <span className="block px-2 py-2">{m.isi?.[p] || <span className="text-slate-300">-</span>}</span>)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      {data && beban.length > 0 && (
        <Card className="rounded-2xl" data-testid="jadwal-beban-panel">
          <button type="button" onClick={() => setBebanBuka((b) => !b)} aria-expanded={bebanBuka} className="w-full p-4 flex items-center gap-2 text-left" data-testid="jadwal-beban-toggle">
            <Users className="h-4 w-4 text-[hsl(224,64%,33%)]" /><h2 className="text-base md:text-lg font-semibold text-slate-900">Beban Pelayan {BULAN[bulan - 1]} {tahun}</h2><span className="text-xs text-slate-500 ml-auto">{beban.length} pelayan · {data.minggu.length} minggu</span>
            {bebanBuka ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
          </button>
          {bebanBuka && (
            <div className="px-4 pb-4" data-testid="jadwal-beban-isi">
              <div className="flex flex-wrap gap-2">
                {beban.map((b) => (
                  <span key={b.nama} title={[...b.posisi].join(", ")} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm ${b.n >= data.minggu.length ? "border-amber-300 bg-amber-50 text-amber-900" : "border-slate-200 bg-slate-50 text-slate-700"}`} data-testid={`jadwal-beban-${b.nama.replace(/\s+/g, "-")}`}>
                    {b.nama}<b className={`rounded-full px-1.5 text-xs ${b.n >= data.minggu.length ? "bg-amber-200" : "bg-white border"}`}>{b.n}×</b>
                  </span>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-2">Kuning = dijadwalkan setiap minggu bulan ini. Arahkan kursor untuk melihat posisinya.</p>
            </div>
          )}
        </Card>
      )}
      {data && canRekapPresensi && <RekapPresensiPelayan bulan={bulan} tahun={tahun} judulBulan={BULAN[bulan - 1]} refreshKey={presensiRefresh} />}
      {canPresensi && <PresensiPelayanDialog tanggal={presensiTgl} onClose={() => setPresensiTgl(null)} onSaved={() => setPresensiRefresh((n) => n + 1)} />}
      {data && REKAP_ROLES.includes(user?.role) && <RekapBebanTahunan tahun={tahun} />}
      {canWrite && data && data.posisi.filter((p) => !data.multi_maks?.[p]).map((p) => (
        <datalist key={p} id={`jadwal-saran-${p.replace(/\s+/g, "-")}`} data-testid={`jadwal-saran-${p.replace(/\s+/g, "-")}`}>
          {saranUntukPosisi(p, saran).map((s) => <option key={s.nama} value={s.nama}>{[s.jabatan_gerejawi ? namaGelar(s) : null, ...(s.bidang || []), ...(s.musik || [])].filter(Boolean).join(", ")}</option>)}
        </datalist>
      ))}
    </div>
  );
}
